import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { Box, Button, Flex, Radio, Switch } from "@chakra-ui/react";
import { useControls } from "src/utils/createControls";
import * as selectors from "src/modules/sound-alerts/duck/selectors";
import * as actions from "src/modules/sound-alerts/duck/actions";
import { SoundAlerts } from "src/modules/sound-alerts/SoundAlerts";
import ListButton from "src/components/ListButton";
import Modal from "src/components/Modal";
import NavigationBar from "src/components/NavigationBar";
import { H3, BodyText2, Subtitle } from "src/atoms/typography";

import { ReactComponent as AlertsIcon } from "src/icons/sound-outlined.svg";
import { ReactComponent as DelayIcon } from "src/icons/clock.svg";
import { ReactComponent as RepeatIcon } from "src/icons/repeat.svg";
import { SoundAlertsDelay } from "../SoundAlertsDelay";

function SoundSelect({ value, checked, onChange, label }) {
  return (
    <Flex as="label" alignItems="center" py="5px">
      <Radio value={value} isChecked={checked} onChange={(e) => onChange(e.target.value)} />
      <Subtitle ml="14px" color="highEmphasis">
        {label}
      </Subtitle>
    </Flex>
  );
}

export default function SoundAlertsSettings({ onClose, onBack }) {
  const { t } = useTranslation();
  const alertDialog = useControls();
  const delayDialog = useControls();
  const repeatDialog = useControls();

  const dispatch = useDispatch();
  const enabled = useSelector(selectors.getEnabled);
  const selectedSound = useSelector(selectors.getSelectedSound);
  const repeat = useSelector(selectors.getRepeat);
  const delay = useSelector(selectors.getDelay);

  const toggleEnabled = () => dispatch(actions.toggleEnabled());
  const setRepeat = (e) => dispatch(actions.setRepeat(e));
  const selectSound = (e) => dispatch(actions.selectSound(e));
  const setDelay = (e) => dispatch(actions.setDelay(e));

  const onAlertChange = (value, alert) => {
    selectSound(value);
    alert.playStartSound();
  };

  return (
    <>
      <NavigationBar headingKey="soundAlerts.heading" onClose={onClose} onBack={onBack} />
      <Box p="24px">
        <H3 width="520px" color="highEmphasis">
          {t("soundAlerts.enable.heading")}
        </H3>
        <Flex color="mediumEmphasis" mt="8px" alignItems="center" justifyContent="space-between">
          <BodyText2>{t("soundAlerts.enable.text")}</BodyText2>
          <Switch isChecked={enabled} onChange={toggleEnabled} />
        </Flex>
        <Flex mt="32px" width="100%" flexWrap="wrap" rowGap="16px" columnGap="24px">
          <ListButton
            onClick={alertDialog.open}
            disabled={!enabled}
            flexBasis="calc(50% - 12px)"
            icon={AlertsIcon}
            iconFill="primary"
            additionalText={t(`soundAlerts.${selectedSound}`)}
            label={t("soundAlerts.select.heading")}
          />
          <ListButton
            onClick={delayDialog.open}
            disabled={!enabled}
            flexBasis="calc(50% - 12px)"
            icon={DelayIcon}
            iconFill="primary"
            additionalText={
              delay ? SoundAlertsDelay.strings[SoundAlertsDelay.keysByValue[delay]] : t("soundAlerts.off")
            }
            label={t("delay")}
          />
          <ListButton
            onClick={repeatDialog.open}
            disabled={!enabled}
            flexBasis="calc(50% - 12px)"
            icon={RepeatIcon}
            iconFill="primary"
            additionalText={repeat ? t("on") : t("soundAlerts.off")}
            label={t("alertRepeat")}
          />
        </Flex>

        <Modal isOpen={alertDialog.isOpen} onClose={alertDialog.close}>
          <Flex width="280px" flexDir="column" rowGap="4px" pt="24px" pl="24px" pr="8px">
            <H3 color="highEmphasis" mb="12px">
              {t("soundAlerts.select.heading")}
            </H3>
            <SoundSelect
              value={SoundAlerts.bold.name}
              label={t(`soundAlerts.${SoundAlerts.bold.name}`)}
              checked={selectedSound === SoundAlerts.bold.name}
              onChange={(value) => onAlertChange(value, SoundAlerts.bold)}
            />
            <SoundSelect
              value={SoundAlerts.discreet.name}
              label={t(`soundAlerts.${SoundAlerts.discreet.name}`)}
              checked={selectedSound === SoundAlerts.discreet.name}
              onChange={(value) => onAlertChange(value, SoundAlerts.discreet)}
            />
            <SoundSelect
              value={SoundAlerts.melodic.name}
              label={t(`soundAlerts.${SoundAlerts.melodic.name}`)}
              checked={selectedSound === SoundAlerts.melodic.name}
              onChange={(value) => onAlertChange(value, SoundAlerts.melodic)}
            />
            <Flex mt="8px" pr="8px" pb="8px" justifyContent="flex-end">
              <Button variant="flatPrimary" onClick={alertDialog.close} minWidth="80px">
                {t("buttons.ok")}
              </Button>
            </Flex>
          </Flex>
        </Modal>

        <Modal isOpen={delayDialog.isOpen} onClose={delayDialog.close}>
          <Flex width="280px" flexDir="column" rowGap="4px" pt="24px" pl="24px" pr="8px">
            <H3 color="highEmphasis" mb="12px">
              {t("delay")}
            </H3>
            <SoundSelect
              value={SoundAlertsDelay.values["2_minutes"]}
              label={SoundAlertsDelay.strings["2_minutes"]}
              checked={delay === SoundAlertsDelay.values["2_minutes"]}
              onChange={(value) => setDelay(Number(value))}
            />
            <SoundSelect
              value={SoundAlertsDelay.values["1_minute"]}
              label={SoundAlertsDelay.strings["1_minute"]}
              checked={delay === SoundAlertsDelay.values["1_minute"]}
              onChange={(value) => setDelay(Number(value))}
            />
            <SoundSelect
              value={SoundAlertsDelay.values["20_seconds"]}
              label={SoundAlertsDelay.strings["20_seconds"]}
              checked={delay === SoundAlertsDelay.values["20_seconds"]}
              onChange={(value) => setDelay(Number(value))}
            />
            <SoundSelect
              value={SoundAlertsDelay.values["5_seconds"]}
              label={SoundAlertsDelay.strings["5_seconds"]}
              checked={delay === SoundAlertsDelay.values["5_seconds"]}
              onChange={(value) => setDelay(Number(value))}
            />
            <SoundSelect value={0} label={t("soundAlerts.off")} checked={!delay} onChange={() => setDelay(0)} />

            <BodyText2 my="16px" color="mediumEmphasis" pr="16px">
              {t("soundAlerts.delay.description")}
            </BodyText2>
          </Flex>
          <Flex pr="8px" pb="8px" justifyContent="flex-end">
            <Button variant="flatPrimary" onClick={delayDialog.close} minWidth="80px">
              {t("buttons.ok")}
            </Button>
          </Flex>
        </Modal>

        <Modal isOpen={repeatDialog.isOpen} onClose={repeatDialog.close}>
          <Flex width="280px" flexDir="column" rowGap="4px" pt="24px" pl="24px" pr="8px">
            <H3 color="highEmphasis" mb="12px">
              {t("alertRepeat")}
            </H3>
            <SoundSelect value={1} label={t("on")} checked={repeat} onChange={() => setRepeat(true)} />
            <SoundSelect value={0} label={t("soundAlerts.off")} checked={!repeat} onChange={() => setRepeat(false)} />
            <BodyText2 my="16px" color="mediumEmphasis" pr="16px">
              {t("soundAlerts.repeat.description")}
            </BodyText2>
          </Flex>
          <Flex pr="8px" pb="8px" justifyContent="flex-end">
            <Button variant="flatPrimary" onClick={repeatDialog.close} minWidth="80px">
              {t("buttons.ok")}
            </Button>
          </Flex>
        </Modal>
      </Box>
    </>
  );
}
